import React, { useMemo, useRef, useState } from "react";
import Select, { ValueType } from "react-select";
import Manual from "./Manual";
import Button from "react-bootstrap/Button";
import useUserRole from "../../../../../utils/hooks/useUserRole";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faExclamationTriangle, 
  faUpload, 
  faSearch, 
  faFileAlt, 
  faInfoCircle,
  faFileExcel
} from "@fortawesome/free-solid-svg-icons";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FileType } from "../../../file-reader/FileReaderHelpers";
import { useDispatch, useSelector } from "react-redux";
import { ApplicationState } from "../../../../../store";
import { PopupDismissalKeys } from "../../../../../store/onboarding/types";
import { isShowConfimationDialog } from "../../../../../utils/NamesUtils";
import { showConfirmDialog } from "../../../../../store/confirm-dialog/actions";
import DataFileReader from "../../../../../containers/student-data-upload/FileReader";

type Props = {
  openSearchForYourAssessmentModal: () => void;
  //openUploadStudentDataModal: () => void;
  handleAcceptFile: () => void;
  uploadCSVArray?: any;
  onHide?: () => void;
  columnsNames?: string[];
};

type FileTypeOption = {
  value: FileType;
  label: string;
};

const FileUnrecognizedContent = (props: Props) => {
  const { openSearchForYourAssessmentModal, onHide, handleAcceptFile, columnsNames } = props;
  const { isDistrictOwner } = useUserRole();
  const dispatch = useDispatch();
  const dataFileReaderRef = useRef<any>(null!);
  // State for file type selection
  const [fileTypeSelected, setFileTypeSelected] = useState<FileTypeOption | null>(null);

  const userDismissPopupDetails =
          useSelector(
            (s: ApplicationState) => s.auth.userInfo?.user_dismiss_pop_up_details || {}
          );

  // Map NJTSS sheet name -> time_period code
  const getTimePeriodForSheet = (sheetName: string): string => {
    if (sheetName === 'Fall Data Entry') return 'boy';
    if (sheetName === 'Winter Data Entry') return 'moy';
    if (sheetName === 'Spring Data Entry') return 'eoy';
    return '';
  };

  const handleFileInputModal = () => {
    if (isShowConfimationDialog(userDismissPopupDetails[PopupDismissalKeys.UPLOAD_DATA_GUIDE])) {
        dispatch(showConfirmDialog({
          onConfirm: () => {
            const fileInput = dataFileReaderRef.current.querySelector('input[type="file"]');
            fileInput?.click();
          },
          centered: true,
          title: "Review Before Uploading",
          timerElipseLeft: true,
          countDownTimer: 5,
          isCustomisedContent: true,
          text: (
            <>
              <p className="text-center">Review your file using the guidance outlined on the page before uploading.</p>
              <p className="font-weight-bold mb-1 ml-4">Quick checks:</p>
              <ul>
                <li>Column names match exactly</li>
                <li>Casing is consistent</li>
                <li>No extra spaces</li>
              </ul>
              <p className="text-center">
                <FontAwesomeIcon 
                  icon={faExclamationTriangle}
                  style={{color:'red'}}
                /> {" "}
                <i>If the file doesn’t match, the upload may fail or be incomplete.</i>
              </p>
            </>
          ),
          confirmButtonText: "Upload File",
          popupKey: PopupDismissalKeys.UPLOAD_DATA_GUIDE
        }))
    } else {
      const fileInput = dataFileReaderRef.current.querySelector('input[type="file"]');
      fileInput?.click();
    }
  }
  
  // File type options for dropdown
  const fileTypeOptions: FileTypeOption[] = [
    { value: FileType.LINKIT, label: 'LinkIt' },
    { value: FileType.DESSA, label: 'DESSA' },
    { value: FileType.ISTEEP, label: 'iSTEEP' },
    { value: FileType.MCLASS, label: 'mClass' },
    { value: FileType.NJTSS, label: 'NJTSS' },
    { value: FileType.DDMS, label: 'DDMS' },
  ];

  // A "ColumnRequirement" can be:
  //  - a plain string: a single expected column, optionally with '|' separated
  //    name variations (any ONE variation satisfies this single requirement).
  //  - a string[]: a group of DISTINCT measurements bundled together (e.g.
  //    LNF, PSF, NWF-CLS, NWF-WRC, WRF, ORF, ORF-Accuracy, Maze, Composite).
  //    Each entry in the array is itself a pipe-separated variation string,
  //    and EACH measurement is validated independently (at least one of its
  //    own variations must be present) rather than the whole group sharing
  //    a single "any match" check.
  type ColumnRequirement = string | string[];

  // Shared measurement group used by LinkIt & NJTSS (same underlying scoring
  // measurements, same header-name variations).
  const LINKIT_NJTSS_MEASUREMENTS: string[] = [
    'LNF (LNF)|LNF',
    'PSF (PSF)|PSF',
    'NWF (CLS)|NWF-CLS|NWF - CLS(NWF - CLS)|NWF - CLS (NWF - CLS)|NWF (NWF-CLS)|NWF: CLS|NWF - CLS',
    'NWF-WRC (NWF-WRC)|NWF - WRC (NWF-WRC)|NWF - WRC (NWF - WRC)|NWF (NWF-WRC)|NWF (WRC)|NWF: WRC|NWF - WRC|NWF-WRC',
    'WRF (WRF)|WRF',
    'ORF (ORF)|ORF (ORF-Words Correct)|ORF (ORF - Words Correct)|ORF (Words Correct)|ORF (ORF Words Correct)|ORF - Words Correct (ORF - Words Correct)|ORF-Words Correct (ORF-Words Correct)',
    'ORF-ACC (ORF-ACC)|ORF (Percent)|ORF (Accuracy)|ORF - Accuracy(ORF - Accuracy)|ORF - Accuracy (ORF - Accuracy)|ORF-Accuracy(ORF-Accuracy)|ORF(ORF - Accuracy)|ORF (ORF - Accuracy)|ORF(ORF-Accuracy)|ORF (ORF- Accuracy)|ORF (ORF -Accuracy)',
    'MAZE|Maze (Maze Adjusted)|MAZE (Maze Adjusted)|Maze (MAZE Adjusted)|MAZE (MAZE Adjusted)|Maze|Maze-Adj|MAZE - Adj|Adj|Maze (Maze)|Maze(Maze)|Maze Adjusted|Maze Adjusted (Maze Adjusted)|Maze Adjusted(Maze Adjusted)',
    'Composite-Score|Composite|Composite - Score|Composite Score',
  ];

  // mClass uses its own fixed (single-variation) header names per measurement.
  const MCLASS_MEASUREMENTS: string[] = [
    'Letter Names (LNF) - Score',
    'Phonemic Awareness (PSF) - Score',
    'Letter Sounds (NWF-CLS) - Score',
    'Decoding (NWF-WRC) - Score',
    'Word Reading (WRF) - Score',
    'Reading Accuracy (ORF-Accu) - Score',
    'Reading Fluency (ORF) - Score',
    'Error Rate (ORF) - Score',
    'Reading Comprehension (Maze) - Score',
  ];

  const exampleColumns: Record<FileType, ColumnRequirement[]> = {
    [FileType.LINKIT]: [
      'ID',
      'Student',
      LINKIT_NJTSS_MEASUREMENTS,
    ],
    [FileType.DESSA]: [
      'StudentId|StudentID|Student ID',
      'StudentFirstName|Student First Name',
      'StudentLastName|Student Last Name',
      'RatingPeriodName|Rating Period Name',
      'RecordForm|Record Form',
    ],
    [FileType.ISTEEP]: [
      'Student ID',
      'First Name',
      'Last Name',
      'School District',
      'CDWD Score',
      'Score',
      'Skill',
      'School',
    ],
    [FileType.MCLASS]: [
      'Student Primary ID',
      'Student First Name',
      'Student Last Name',
      MCLASS_MEASUREMENTS,
    ],
    [FileType.NJTSS]: [
      'Student ID#',
      'First Name',
      'Last Name',
      LINKIT_NJTSS_MEASUREMENTS,
    ],
    [FileType.DDMS]: [
      "Student_ID",
      "First",
      "Last",
    ],
    [FileType.GENERIC]: []
    };

  // Get matched and unmatched columns based on selected file type
  const detectDdmsGrades = (headers: string[]): Set<string> => {
    const detectedGrades = new Set<string>();
    headers.forEach((col) => {
      if (col.includes('k') && (col.includes('_k_') || col.includes('_k') || col.endsWith('k'))) {
        detectedGrades.add('k');
      }
      ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th'].forEach((grade) => {
        if (col.includes(grade)) {
          detectedGrades.add(grade);
        }
      });
    });
    return detectedGrades;
  };

  // Shared match-result shape used for both single-sheet file types and
  // each individual NJTSS sheet.
  type MatchResult = {
    matched: string[];
    unmatched: string[];
    missingMeasurements: string[];
    categoryTotal: number;
    categoryMatched: number;
    totalMissing: number;
  };


  const computeMatchResult = (
    headers: string[],
    requirements: ColumnRequirement[],
    detectedGrades?: Set<string>
  ): MatchResult => {
    const matched: string[] = [];
    const unmatched: string[] = [];
    const missingMeasurements: string[] = [];
    let anyMeasurementMatched = false;

    const flattened: { column: string; isMeasurement: boolean }[] = requirements.reduce<
      { column: string; isMeasurement: boolean }[]
    >(
      (acc, requirement) =>
        Array.isArray(requirement)
          ? acc.concat(requirement.map((column) => ({ column, isMeasurement: true })))
          : acc.concat([{ column: requirement, isMeasurement: false }]),
      []
    );

    const gradePatterns = ['k', '1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th'];

    flattened.forEach(({ column, isMeasurement }) => {
      const variations = column.split('|').map((v) => v.trim());
      const isMatched = variations.some((variation) => headers.includes(variation));

      if (isMatched) {
        matched.push(variations[0]);
        if (isMeasurement) {
          anyMeasurementMatched = true;
        }
        return;
      }

      // DDMS: skip grade-specific columns for grades not present in the file.
      if (detectedGrades && detectedGrades.size > 0) {
        let hasGradeSpecificVariation = false;
        let isForDetectedGrade = false;
        variations.forEach((variation) => {
          for (const grade of gradePatterns) {
            if (variation.includes(grade)) {
              hasGradeSpecificVariation = true;
              if (detectedGrades.has(grade)) {
                isForDetectedGrade = true;
                break;
              }
            }
          }
        });
        if (hasGradeSpecificVariation && !isForDetectedGrade) {
          return;
        }
      }

      if (isMeasurement) {
        missingMeasurements.push(variations[0]);
      } else {
        unmatched.push(column);
      }
    });

    // If at least one measurement was found, the "at least one" requirement
    // is satisfied - don't flag any measurements as missing.
    const resolvedMissingMeasurements = anyMeasurementMatched ? [] : missingMeasurements;

    // Category counts treat the whole measurement group as a SINGLE
    // requirement (matching how it's displayed as one combined badge).
    const categoryTotal = requirements.length;
    const categoryMatched = requirements.reduce((count, requirement) => {
      if (Array.isArray(requirement)) {
        return count + (resolvedMissingMeasurements.length === 0 ? 1 : 0);
      }
      return count + (unmatched.includes(requirement) ? 0 : 1);
    }, 0);
    const totalMissing = unmatched.length + (resolvedMissingMeasurements.length > 0 ? 1 : 0);

    return {
      matched,
      unmatched,
      missingMeasurements: resolvedMissingMeasurements,
      categoryTotal,
      categoryMatched,
      totalMissing,
    };
  };

  // Single-sheet match result (all file types EXCEPT NJTSS have exactly one
  // sheet, so this is computed directly off the flat `columnsNames` prop).
  const columnMatchResults: MatchResult = useMemo(() => {
    if (!fileTypeSelected || !columnsNames || columnsNames.length === 0) {
      return { matched: [], unmatched: [], missingMeasurements: [], categoryTotal: 0, categoryMatched: 0, totalMissing: 0 };
    }
    const requirements = exampleColumns[fileTypeSelected.value] || [];
    const detectedGrades =
      fileTypeSelected.value === FileType.DDMS ? detectDdmsGrades(columnsNames) : undefined;
    return computeMatchResult(columnsNames, requirements, detectedGrades);
  }, [fileTypeSelected, columnsNames]);

  // ===== NJTSS multi-sheet validation =====
  // NJTSS is the ONLY file type whose uploads can contain multiple sheets
  // (e.g. "Winter Data Entry", "Spring Data Entry"). Each sheet is validated
  // independently using the same `computeMatchResult` logic, plus its own
  // grade / year / time period checks.
  type NjtssSheetInfo = MatchResult & {
    sheetName: string;
    timePeriod: string;
    isMissingGrade: boolean;
    isMissingYear: boolean;
  };

  const njtssSheets: NjtssSheetInfo[] = useMemo(() => {
    if (fileTypeSelected?.value !== FileType.NJTSS || !props.uploadCSVArray) {
      return [];
    }

    const requirements = exampleColumns[FileType.NJTSS] || [];
    const results: NjtssSheetInfo[] = [];

    for (const sheetName in props.uploadCSVArray) {
      if (!Object.prototype.hasOwnProperty.call(props.uploadCSVArray, sheetName)) {
        continue;
      }
      const sheet = props.uploadCSVArray[sheetName];
      // Skip sheets that have no data at all
      if (!sheet?.rawData?.length) {
        continue;
      }

      const headerRow = sheet.rawData[0] || {};
      const sheetHeaders = Object.keys(headerRow).map((h) => h.trim());
      const matchResult = computeMatchResult(sheetHeaders, requirements);

      results.push({
        ...matchResult,
        sheetName,
        timePeriod: getTimePeriodForSheet(sheetName),
        isMissingGrade: !sheet.grade,
        isMissingYear: !sheet.year,
      });
    }

    return results;
  }, [fileTypeSelected, props.uploadCSVArray]);

  const isNjtss = fileTypeSelected?.value === FileType.NJTSS;

  // Sheets missing grade / time period / year (required details)
  const sheetsMissingDetails = njtssSheets.filter(
    (s) => s.isMissingGrade || s.isMissingYear || !s.timePeriod
  );

  // Sheets missing one or more required columns
  const sheetsMissingColumns = njtssSheets.filter((s) => s.totalMissing > 0);

  const isNjtssMultiSheetValid =
    !isNjtss || (sheetsMissingDetails.length === 0 && sheetsMissingColumns.length === 0);

  // Render a single plain-text badge (e.g. "Grade", "Year", "Time Period").
  const renderPlainBadge = (label: string, key: string | number) => (
    <span className="missing-column-badge" key={key}>
      <FontAwesomeIcon icon={faExclamationTriangle} className="fa-icon" />
      {label}
    </span>
  );

  // Render a single missing-column badge for a plain (non-measurement) column.
  const renderMissingColumnBadge = (col: string, idx: number) => {
    const variations = col.split('|').map((v) => v.trim());
    return renderPlainBadge(variations[0], idx);
  };

  // Render ONE combined badge for all missing measurements: "At least one
  // measurement required", with a tooltip listing every missing measurement
  // name, comma separated.
  const renderMissingMeasurementsBadge = (measurements: string[], key: string | number) => {
    if (measurements.length === 0) {
      return null;
    }

    const badge = (
      <span className="missing-column-badge">
        <FontAwesomeIcon icon={faExclamationTriangle} className="fa-icon" />
        At least 1 measurement
      </span>
    );

    return (
      <OverlayTrigger
        key={key}
        placement="top"
        overlay={
          <Tooltip id={`measurement-tooltip-${key}`}>
            {measurements.join(', ')}
          </Tooltip>
        }
      >
        {badge}
      </OverlayTrigger>
    );
  };

  // Handle file type selection change
  const handleFileTypeChange = (option: ValueType<FileTypeOption, false>) => {
    const selectedOption = option as FileTypeOption;
    setFileTypeSelected(selectedOption);
  }; 
  return (
    <div className="file-unrecognized-container">
      {/* Header with warning icon */}
      <div className="mb-3 text-center">
          <h3 className="file-unrecognized-title">
            Confirm Source Type
          </h3>
          <p className="file-unrecognized-subtitle">
            We couldn't automatically identify the source from your file.
            Please confirm the source type below.
          </p>
      </div>

      {/* Source Type Selection */}
      <div className="mb-4">
        <div className="row g-3">
          {/* Dropdown Column */}
          <div className="col-md-4">
            <div className="assessment-type-matched">
              <label className="form-label file-unrecognized-form-label">
                Source Type
              </label>
              <Select
                value={fileTypeSelected}
                onChange={handleFileTypeChange}
                options={fileTypeOptions}
                placeholder="Select"
                className="text-start"
                isClearable
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: '8px',
                    padding: '0.25rem',
                    fontSize: '0.95rem',
                    borderColor: '#ced4da',
                    boxShadow: 'none',
                    '&:hover': {
                      borderColor: '#adb5bd'
                    }
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isSelected ? '#0d6efd' : state.isFocused ? '#e7f3ff' : 'white',
                    color: state.isSelected ? 'white' : '#212529',
                    fontSize: '0.95rem'
                  })
                }}
              />
            </div>
          </div>

          {/* Status Cards Column (single-sheet file types) */}
          {fileTypeSelected && columnsNames && columnsNames.length > 0 && !isNjtss && (
            <>
              {/* Matched Columns Card */}
              <div className="col-md-4">
                <div className="status-card-matched">
                  <div className="d-flex align-items-center mb-2">
                    <div className="status-icon-circle success">
                      <span className="status-icon-text">✓</span>
                    </div>
                    <div>
                      <div className="status-count success">
                        {columnMatchResults.categoryMatched} of {columnMatchResults.categoryTotal}
                      </div>
                      <div className="status-label success">
                        Required columns detected
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Missing Columns Card */}
              <div className="col-md-4">
                <div className={`status-card-missing ${columnMatchResults.totalMissing > 0 ? 'has-missing' : 'all-matched'}`}>
                  <div className="d-flex align-items-center mb-2">
                    <div className={`status-icon-circle ${columnMatchResults.totalMissing > 0 ? 'warning' : 'success'}`}>
                      <span className="status-icon-text">
                        {columnMatchResults.totalMissing > 0 ? '⚠' : '✓'}
                      </span>
                    </div>
                    <div>
                      <div className={`status-count ${columnMatchResults.totalMissing > 0 ? 'warning' : 'success'}`}>
                        {columnMatchResults.totalMissing}
                      </div>
                      <div className={`status-label ${columnMatchResults.totalMissing > 0 ? 'warning' : 'success'}`}>
                        Columns missing
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* NJTSS Per-Sheet Status Cards: same visual theme as the single-sheet
            status cards above, but rendered once per sheet since NJTSS is the
            only file type that can contain multiple sheets (e.g. Winter Data
            Entry, Spring Data Entry) with independent data. */}
        {isNjtss && njtssSheets.length > 0 && (
          <div className="njtss-sheet-summary-list mt-3">
            {njtssSheets.map((sheet, idx) => (
              <div className="njtss-sheet-summary-card" key={idx}>
                <div className="njtss-sheet-summary-header">
                  <FontAwesomeIcon icon={faFileExcel} className="fa-icon" />
                  <span>{sheet.sheetName}</span>
                </div>
                <div className="row g-3">
                  <div className="col-sm-6">
                    <div className="status-card-matched">
                      <div className="d-flex align-items-center mb-2">
                        <div className="status-icon-circle success">
                          <span className="status-icon-text">✓</span>
                        </div>
                        <div>
                          <div className="status-count success">
                            {sheet.categoryMatched} of {sheet.categoryTotal}
                          </div>
                          <div className="status-label success">
                            Required columns detected
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className={`status-card-missing ${sheet.totalMissing > 0 ? 'has-missing' : 'all-matched'}`}>
                      <div className="d-flex align-items-center mb-2">
                        <div className={`status-icon-circle ${sheet.totalMissing > 0 ? 'warning' : 'success'}`}>
                          <span className="status-icon-text">
                            {sheet.totalMissing > 0 ? '⚠' : '✓'}
                          </span>
                        </div>
                        <div>
                          <div className={`status-count ${sheet.totalMissing > 0 ? 'warning' : 'success'}`}>
                            {sheet.totalMissing}
                          </div>
                          <div className={`status-label ${sheet.totalMissing > 0 ? 'warning' : 'success'}`}>
                            Columns missing
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* NJTSS Multi-Sheet Validation: missing grade/time period/year per sheet */}
      {isNjtss ? (
        <div className="row g-3 mb-4">
        { sheetsMissingColumns.length > 0
          ?
          <div className={sheetsMissingDetails.length > 0 ? "col-md-6" : "col-12"}>
          <div className="missing-columns-container h-100">
            <div className="missing-columns-title">
              Missing Columns by Sheet
            </div>
            <div className="missing-columns-description">
              {sheetsMissingColumns.length} of {njtssSheets.length} sheet{njtssSheets.length === 1 ? '' : 's'} {sheetsMissingColumns.length === 1 ? 'is' : 'are'} missing required columns:
            </div>
            <div className="d-flex flex-column gap-3">
              {sheetsMissingColumns.map((sheet, idx) => (
                <div key={idx}className="d-flex flex-wrap align-items-center gap-2">
                  <span className="fw-bold">{sheet.sheetName}:</span>
                  <div className="d-flex flex-wrap gap-2">
                    {sheet.unmatched.map((col, colIdx) =>
                      renderMissingColumnBadge(col, colIdx)
                    )}
                    {renderMissingMeasurementsBadge(sheet.missingMeasurements, `${idx}-measurements`)}
                  </div>
                </div>
              ))}
            </div>
          </div>
          </div>
        : null}
        { sheetsMissingDetails.length > 0
          ?
          <div className={sheetsMissingColumns.length > 0 ? "col-md-6" : "col-12"}>
          <div className="missing-columns-container h-100">
            <div className="missing-columns-title">
              Missing Grade / Time Period / Year
            </div>
            <div className="missing-columns-description">
              The following sheets are missing required details:
            </div>
            <div className="d-flex flex-column gap-2">
              {sheetsMissingDetails.map((sheet, idx) => (
                <div key={idx} className="d-flex flex-wrap align-items-center gap-2">
                  <span className="fw-bold">{sheet.sheetName}:</span>
                  {sheet.isMissingGrade && renderPlainBadge('Grade', 'grade')}
                  {sheet.isMissingYear && renderPlainBadge('Year', 'year')}
                  {!sheet.timePeriod && renderPlainBadge('Time Period', 'time-period')}
                </div>
              ))}
            </div>
          </div>
          </div>
        : null }
        </div>
      )
      : 
      fileTypeSelected && columnMatchResults.totalMissing > 0 && (
        <div className="mb-4 missing-columns-container">
          <div className="missing-columns-title">
            Missing Columns
          </div>
          <div className="missing-columns-description">
            The following required columns were not found:
          </div>
          <div className="d-flex flex-wrap gap-2">
            {columnMatchResults.unmatched.map((col, idx) =>
              renderMissingColumnBadge(col, idx)
            )}
            {renderMissingMeasurementsBadge(columnMatchResults.missingMeasurements, 'generic-measurements')}
          </div>
        </div>
      )}

      {/* Fix & Re-upload File Card */}
      {fileTypeSelected && ((isNjtss ? !isNjtssMultiSheetValid : columnMatchResults.totalMissing > 0)) && (
        <div className="mb-3 fix-upload-card">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <div className="action-icon-circle">
                <FontAwesomeIcon icon={faUpload} style={{ color: 'white', fontSize: '1.5rem' }} />
              </div>
              <div>
                <div className="action-title">
                  Fix & Re-upload File
                </div>
                <div className="action-description">
                  Correct the missing columns and upload the file again for automatic validation.
                </div>
              </div>
            </div>
            <div>
            <label className="blueBtnMd" data-cy="upload-data-csv-file" onClick={handleFileInputModal}>
              Fix & Re-upload File
            </label>
            <span ref={dataFileReaderRef}><DataFileReader/></span>
            </div>
          </div>
        </div>
      )}

      {/* Divider */}
      <div className="content-divider-container">
        <div className="content-divider-line"></div>
        <span className="content-divider-text">
          OR CONTINUE WITHOUT FIXING
        </span>
      </div>
      <div className="divider-subtext">
        You can still proceed using one of the options below.
      </div>

      {/* Action Options */}
      <div className="row g-3 mb-4">
        {/* Search Assessment */}
        <div className="col-md-6">
          <div onClick={openSearchForYourAssessmentModal} className="action-card search">
            <div className="d-flex align-items-start">
              <div className="action-icon-circle">
                <FontAwesomeIcon icon={faSearch} style={{ color: 'white', fontSize: '1.5rem' }} />
              </div>
              <div className="action-card-content">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <span className="action-title">
                    Search Assessment
                  </span>
                </div>
                <div className="action-description">
                  Find and select a matching assessment from the assessment library.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Create Custom Assessment */}
        <div className="col-md-6">
          <div
            onClick={isNjtssMultiSheetValid ? handleAcceptFile : undefined}
            className="action-card create"
            style={!isNjtssMultiSheetValid ? { opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none' } : undefined}
          >
            <div className="d-flex align-items-start">
              <div className="action-icon-circle">
                <FontAwesomeIcon icon={faFileAlt} style={{ color: 'white', fontSize: '1.5rem' }} />
              </div>
              <div className="action-card-content">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <span className="action-title">
                    Create Custom Assessment
                  </span>
                </div>
                <div className="action-description">
                  Continue using this file and create a new custom assessment based on your data.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <hr />
      {/* Cancel Button */}
      <div className="btn-actions">
        <Button
          variant="outline-secondary"
          onClick={onHide}
          className="whiteBtnSm"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default FileUnrecognizedContent;